import React, { useEffect, Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { SecondaryText } from 'Components/Texts';
import { setLocation } from 'Utils/Path';
import { SubmitButton } from 'Components/Fields/Buttons';
import { getDefaultConfig } from 'Utils/Store';
import { getEntitlements } from 'State/User';
import { Navigate } from 'react-router-dom';
import { logger } from 'Utils/Logger';
import { fetchSubscriptionStatus, fetchSubscriptions } from 'State/Subscriptions';
import { useStore } from 'Hooks/useStore';
import './CancelPlanContainer.css';

export const CancelConfirmationContainer = () => {
  const navigate = useNavigate();
  const { rootDomain } = getDefaultConfig();
  const { nextBillInfo } = useStore('payments');
  const { routes } = useStore('appConfig');
  const subscription = useStore('subscription');

  // Continue showing immediate cancellation if user is now churned, since an immediate
  // cancelation will cause the user to update to a churned state while on this screen.
  const isImmediateCanceled = subscription.isImmediateCancel || subscription.isChurnedUser;

  useEffect(() => {
    fetchSubscriptions();
    fetchSubscriptionStatus();
    getEntitlements();
  }, []);

  const validityTillFormatted = useMemo(() => {
    if (nextBillInfo && nextBillInfo.nextBillingDateTime) {
      let tillDate = nextBillInfo.nextBillingDateTime;
      if (subscription.isTrialistUser === true || isImmediateCanceled) {
        tillDate = isImmediateCanceled ? Date.now() : subscription.validityTill;
      }
      logger.started('cancel confirm date', nextBillInfo);
      let validityTill = new Date(tillDate);
      if (!isImmediateCanceled) {
        // add extra day to cancel date: https://fng-jira.fox.com/browse/DCGC-2404
        validityTill = validityTill.setDate(validityTill.getDate() + 1);
      }
      const validityTillFormatted = new Date(validityTill).toLocaleDateString('en-US');
      logger.completed('cancel confirm date', { validityTillFormatted });
      return validityTillFormatted;
    }
    return undefined;
  }, [nextBillInfo, isImmediateCanceled, subscription]);

  const handleContinue = () => {
    if (isImmediateCanceled) {
      return navigate(routes.myAccount);
    }

    return setLocation(rootDomain);
  };

  const confirmFirstMessage = <Fragment>An email confirmation will be sent to you.</Fragment>;

  const confirmSecondMessage = `Your Fox Nation subscription has been canceled effective${
    isImmediateCanceled ? ' immediately' : ''
  } ${validityTillFormatted}. ${
    isImmediateCanceled ? '' : 'Until then, you can continue to enjoy exclusive daily shows.'
  }`;

  if (!nextBillInfo) {
    return <Navigate to={routes.myAccount} replace />;
  }

  return (
    <CenterFrameLayout title="Your plan has been canceled" showLoader={false}>
      <div className="centerFormWide">
        <div className="cancelContainer">
          <SecondaryText alignText="centerText" text={confirmFirstMessage} className="emailConfirmMsg" />
          <SecondaryText alignText="centerText" text={confirmSecondMessage} className="cancelPlanMsg" />
          <SubmitButton
            className="continueWatching hollow"
            modernLayout
            text={isImmediateCanceled ? 'Back to My Account' : 'Continue Watching Now'}
            onClick={handleContinue}
          />
        </div>
      </div>
    </CenterFrameLayout>
  );
};
